// Share / revoke the public link for "every open task on this item assigned to
// X". A thin wrapper over ShareLinkModal, like ShareTripModal: the five-state
// machine (re-query on every open, at most one row per key, revoke = delete the
// row) is the shared part, and everything below is what this dialog is about.
// The list is LIVE -- reassigning, completing or adding tasks changes what the
// link shows, and the dialog says so.
//
// IT CARRIES THE PHOTOS. A task's attachments are served to the viewer as
// presigned S3 URLs, which means this link stores an `identityId` for the same
// reason ShareLogbookTripModal does: the Lambda finds them under
// `private/<identityId>/` and that id is minted by GetId against the user's own
// token, so it is not derivable server-side and is captured here at share time.
// Failing to resolve it is NOT fatal -- the right outcome is a link with no
// photos, not a failed link. The same goes for the notification address.
//
// IT CAN BE MADE EDITABLE, and that is the control below. The holder can then
// correct a task's fields, complete it, add photos and remove photos they added.
// That is a materially different thing to hand out than a read-only link, and
// this dialog is the only place that can say so before it is handed out --
// which is why the switch names the destructive part out loud.
//
// CREATED OFF, deliberately unlike ShareLogbookTripModal's `allowUploads: true`.
// Letting a stranger rewrite and delete tasks is a bigger grant than letting
// them add a photo to a logbook day, and the owner should make it here,
// knowingly, once the link exists.

import { useCallback, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { fetchUserAttributes } from 'aws-amplify/auth';
import ShareLinkModal from '../trip/ShareLinkModal';
import {
  createAssignedTasksShare,
  getAssignedTasksShareForAssignee,
  deleteAssignedTasksShare,
  updateAssignedTasksShareEdits,
} from '../../utilities/Database.mjs';
import { warmAuthSession } from '../../auth/sessionWarm';
import { fullUrlForSharedAssignedTasks } from '../../fleet-shared/Urls.mjs';
import { shareTasksWriteAvailable } from '../../utilities/shareTasksWrite.mjs';

// Deliberately says neither "read-only" nor "editable": which one a link is
// depends on the switch below, and a line of copy that contradicts a control two
// inches under it is worse than one that says less.
const ACTIVE_INTRO = 'Anyone with this link can open this task list — no account needed:';

const REVOKED_NOTE =
  'Link revoked. Anyone holding it now sees that this task list is no longer shared.';

const ACTIVE_NOTE =
  'The link shows the current list: tasks assigned to this name later appear, and ' +
  'tasks completed or reassigned drop off. Task names, notes, due dates and any ' +
  'photos attached to them are visible.';

export default function ShareAssignedTasksModal({ show, onHide, itemId, assignee, itemName }) {
  const description =
    `Anyone with the link can view the open tasks on ${itemName || 'this item'} assigned ` +
    `to ${assignee} — their names, notes, due dates, counter readings and attached ` +
    'photos — with no account needed. The link always shows the list as it currently ' +
    'is, and you can revoke it here at any time.';

  const load = useCallback(
    () => getAssignedTasksShareForAssignee(itemId, assignee),
    [itemId, assignee]
  );

  const create = useCallback(async () => {
    // Both best-effort and deliberately not behind a guard that could fail the
    // create: a share with no photos is a working share, and so is one that
    // cannot tell you about a change.
    const session = await warmAuthSession().catch(() => null);
    const attributes = await fetchUserAttributes().catch(() => null);

    return createAssignedTasksShare(itemId, assignee, {
      identityId: session?.identityId ?? null,
      notifyEmail: attributes?.email ?? null,
    });
  }, [itemId, assignee]);

  const revoke = useCallback((share) => deleteAssignedTasksShare(share.id), []);
  const urlFor = useCallback((share) => fullUrlForSharedAssignedTasks(share.id), []);

  // Only offered once a link exists: there is nothing to configure about a link
  // that has not been created.
  const extras = useCallback(
    (share) => (share && shareTasksWriteAvailable() ? <EditsToggle share={share} /> : null),
    []
  );

  return (
    <ShareLinkModal
      show={show}
      onHide={onHide}
      title={`Share tasks assigned to ${assignee}`}
      description={description}
      activeIntro={ACTIVE_INTRO}
      activeNote={ACTIVE_NOTE}
      revokedNote={REVOKED_NOTE}
      load={load}
      create={create}
      revoke={revoke}
      urlFor={urlFor}
      extras={extras}
    />
  );
}

// "Let this person edit these tasks."
//
// OPTIMISTIC, and it has to be: the switch is the whole feedback, so waiting for
// a round trip before moving it reads as a dead control. A failed write puts it
// back and says so, which is the honest version of the same thing.
//
// `allowEdits === true` is the read rule, spelled here exactly as the Lambda
// spells it -- NULLISH MEANS OFF, the opposite of the logbook share's
// allowUploads, because every link written before this column existed was handed
// out as read-only. See the schema comment for the full argument.
//
// The copy NAMES the destructive part. "Edit" sounds smaller than it is: a
// completion removes a one-off task from the owner's list outright, and there is
// no undo for that anywhere in the app.
function EditsToggle({ share }) {
  const [allowed, setAllowed] = useState(share.allowEdits === true);
  const [error, setError] = useState(null);

  const change = async (next) => {
    setAllowed(next);
    setError(null);
    try {
      await updateAssignedTasksShareEdits(share.id, { allowEdits: next });
    } catch (err) {
      setAllowed(!next);
      setError(err);
    }
  };

  return (
    <div className="border-top mt-3 pt-3">
      <Form.Check
        type="switch"
        id="share-tasks-allow-edits"
        label="Let this person edit these tasks"
        checked={allowed}
        onChange={(e) => change(e.target.checked)}
      />
      <p className="text-muted small mb-0 mt-2">
        {allowed
          ? share.notifyEmail
            ? `Anyone with the link can change a task's details, add photos, and mark a task
               complete — which rolls a recurring task forward and removes a one-off from
               your list. We'll email ${share.notifyEmail} when they do.`
            : `Anyone with the link can change a task's details, add photos, and mark a task
               complete — which rolls a recurring task forward and removes a one-off from
               your list. We don't have an email address for this link, so you won't be
               notified.`
          : 'The link is read-only. Nobody can change these tasks.'}
      </p>
      {error && (
        <p className="text-danger small mb-0 mt-2">
          Couldn&apos;t change that just now. Please try again.
        </p>
      )}
    </div>
  );
}
